import React, { useState } from 'react';
import { Card, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { Languages, Copy, Check } from 'lucide-react';
import { showSuccess, showError } from '../../../utils/notifications';
import api from '../../../services/api';

interface Translation {
    language: string;
    text: string;
    copied: boolean;
}

const LANGUAGES = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
];

const Translations = () => {
    const [inputText, setInputText] = useState<string>('');
    const [selectedLanguages, setSelectedLanguages] = useState<string[]>(['en', 'es', 'de', 'fr', 'nl']);
    const [translations, setTranslations] = useState<Translation[]>([]);
    const [loading, setLoading] = useState(false);

    const handleLanguageToggle = (languageCode: string) => {
        if (selectedLanguages.includes(languageCode)) {
            setSelectedLanguages(selectedLanguages.filter(lang => lang !== languageCode));
        } else {
            setSelectedLanguages([...selectedLanguages, languageCode]);
        }
    };

    const handleTranslate = async () => {
        if (!inputText.trim()) {
            showError('Error', 'Please enter text to translate');
            return;
        }

        if (selectedLanguages.length === 0) {
            showError('Error', 'Please select at least one language');
            return;
        }

        try {
            setLoading(true);
            const response = await api.post<{
                success: boolean;
                original_text: string;
                translations: { [key: string]: string };
                error?: string;
            }>('/ia/translations', {
                text: inputText,
                languages: selectedLanguages,
            });

            if (response.success && response.translations) {
                const translationArray: Translation[] = Object.entries(response.translations).map(([lang, text]) => ({
                    language: lang,
                    text,
                    copied: false,
                }));
                setTranslations(translationArray);
                showSuccess('Success', 'Text translated successfully!');
            } else {
                showError('Error', response.error || 'Translation failed');
            }
        } catch (error: any) {
            showError('Error', error.response?.data?.error || 'Failed to translate text');
            console.error('Translation error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = (index: number, text: string) => {
        navigator.clipboard.writeText(text);
        const updatedTranslations = [...translations];
        updatedTranslations[index].copied = true;
        setTranslations(updatedTranslations);
        showSuccess('Copied!', 'Translation copied to clipboard');

        setTimeout(() => {
            const resetTranslations = [...translations];
            resetTranslations[index].copied = false;
            setTranslations(resetTranslations);
        }, 2000);
    };

    const getLanguageInfo = (code: string) => {
        return LANGUAGES.find(lang => lang.code === code) || { name: code, flag: '🌐' };
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">AI Translations</h4>
                <Languages size={32} className="text-primary" />
            </div>

            <div className="row">
                {/* Input Section */}
                <div className="col-md-5">
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white border-bottom">
                            <strong>Input Text</strong>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Text to Translate <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={8}
                                        value={inputText}
                                        onChange={(e) => setInputText(e.target.value)}
                                        placeholder="Enter text in any language (e.g., Portuguese, English, etc.)"
                                        style={{ fontSize: '1rem', lineHeight: '1.6' }}
                                    />
                                    <Form.Text className="text-muted">
                                        The AI will automatically detect the source language
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Target Languages <span className="text-danger">*</span>
                                    </Form.Label>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {LANGUAGES.map((lang) => (
                                            <Form.Check
                                                key={lang.code}
                                                type="checkbox"
                                                id={`lang-${lang.code}`}
                                                label={`${lang.flag} ${lang.name}`}
                                                checked={selectedLanguages.includes(lang.code)}
                                                onChange={() => handleLanguageToggle(lang.code)}
                                                style={{
                                                    padding: '8px 12px',
                                                    borderRadius: '8px',
                                                    backgroundColor: selectedLanguages.includes(lang.code) ? '#e7f3ff' : '#f8f9fa',
                                                    border: `2px solid ${selectedLanguages.includes(lang.code) ? '#0d6efd' : '#dee2e6'}`,
                                                }}
                                            />
                                        ))}
                                    </div>
                                    <Form.Text className="text-muted">
                                        Select one or more languages to translate to
                                    </Form.Text>
                                </Form.Group>

                                <Button
                                    variant="primary"
                                    onClick={handleTranslate}
                                    disabled={loading || !inputText.trim() || selectedLanguages.length === 0}
                                    className="w-100"
                                >
                                    {loading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Translating...
                                        </>
                                    ) : (
                                        <>
                                            <Languages size={18} className="me-2" />
                                            Translate
                                        </>
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>

                {/* Translations Section */}
                <div className="col-md-7">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <strong>Translations</strong>
                            {translations.length > 0 && (
                                <Badge bg="primary" className="ms-2">
                                    {translations.length}
                                </Badge>
                            )}
                        </Card.Header>
                        <Card.Body>
                            {translations.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <Languages size={48} className="mb-3" style={{ opacity: 0.3 }} />
                                    <p>Enter text and click "Translate" to see results here</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-3">
                                    {/* Original Text */}
                                    <div
                                        style={{
                                            backgroundColor: '#f8f9fa',
                                            padding: '16px',
                                            borderRadius: '8px',
                                            border: '2px solid #dee2e6',
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <Badge bg="secondary">Original Text</Badge>
                                        </div>
                                        <div
                                            style={{
                                                whiteSpace: 'pre-wrap',
                                                fontFamily: 'system-ui, -apple-system, sans-serif',
                                                fontSize: '0.95rem',
                                                lineHeight: '1.6',
                                            }}
                                        >
                                            {inputText}
                                        </div>
                                    </div>

                                    {/* Translations */}
                                    {translations.map((translation, index) => {
                                        const langInfo = getLanguageInfo(translation.language);
                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    backgroundColor: '#fff',
                                                    padding: '16px',
                                                    borderRadius: '8px',
                                                    border: '2px solid #0d6efd',
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <Badge bg="primary">
                                                        {langInfo.flag} {langInfo.name}
                                                    </Badge>
                                                    <Button
                                                        size="sm"
                                                        variant={translation.copied ? 'success' : 'outline-primary'}
                                                        onClick={() => handleCopy(index, translation.text)}
                                                    >
                                                        {translation.copied ? (
                                                            <>
                                                                <Check size={14} className="me-1" />
                                                                Copied!
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Copy size={14} className="me-1" />
                                                                Copy
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                                <div
                                                    style={{
                                                        whiteSpace: 'pre-wrap',
                                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                                        fontSize: '0.95rem',
                                                        lineHeight: '1.6',
                                                    }}
                                                >
                                                    {translation.text}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Translations;
